import { NoteIndexEntry } from '~/modules/songs/utils/note-index';

export type VocalRangeFit = 'comfortable' | 'a stretch' | 'out of reach';

/** Semitones a singer can be short by and still get through it. */
const STRETCH_TOLERANCE = 3;

export interface VocalRange {
  low: number;
  high: number;
}

/**
 * Whether a song's line fits a singer's range.
 *
 * Only the *width* of the line is compared, never where it sits. The game scores by pitch class -
 * `calcDistanceBetweenPitches` reduces both notes modulo 12 - so singing the whole song an octave
 * down scores exactly the same. What a singer cannot do is cover a line that reaches further than
 * their voice does, whichever octave they start in.
 */
export const vocalRangeFit = (song: Pick<NoteIndexEntry, 'span'>, range: VocalRange): VocalRangeFit => {
  const singerSpan = range.high - range.low;
  const shortfall = song.span - singerSpan;

  if (shortfall <= 0) return 'comfortable';
  if (shortfall <= STRETCH_TOLERANCE) return 'a stretch';

  return 'out of reach';
};

export const describeVocalRangeFit = (fit: VocalRangeFit) =>
  ({
    comfortable: 'Fits your range',
    'a stretch': 'Just past your range',
    'out of reach': 'Reaches further than your range',
  })[fit];
